import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PortalShell, PortalMessage } from "@/components/portal/PortalShell";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking" });

  useEffect(() => {
    if (!sessionId) return undefined;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data?.payment_status === "paid") return setState({ status: "paid", token: data.invoice_token });
        if (data?.payment_status === "expired") return setState({ status: "expired", token: data.invoice_token });
      } catch (e) { /* keep polling */ }
      tries += 1;
      if (tries < 15) timer = setTimeout(poll, 2000);
      else setState({ status: "timeout" });
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <PortalShell testId="payment-success-page">
      {!sessionId && <PortalMessage title="Missing payment" text="We couldn't find this payment." />}
      {sessionId && state.status === "checking" && (
        <div className="flex items-center justify-center gap-3 text-rc-body" data-testid="payment-checking"><Loader2 className="h-5 w-5 animate-spin text-rc-copper" /> Confirming your payment…</div>
      )}
      {state.status === "paid" && (
        <div className="rounded-[24px] border border-rc-line bg-rc-surface p-8 text-center" data-testid="payment-paid">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
          <p className="mt-4 font-display text-4xl">Payment received</p>
          <p className="mt-2 text-rc-body">Thank you! A receipt has been sent to your email by Stripe.</p>
          {state.token && <Link to={`/i/${state.token}`} className="mt-6 inline-block text-rc-copper underline underline-offset-4" data-testid="payment-back-invoice">View your invoice</Link>}
        </div>
      )}
      {state.status === "expired" && <PortalMessage title="Payment session expired" text="Please return to your invoice and try again." />}
      {state.status === "timeout" && <PortalMessage title="Still processing" text="Your payment is being confirmed. Please check your invoice link again in a few minutes." />}
    </PortalShell>
  );
}
